import { Check, Copy } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { formatInt } from "@/lib/format"

/**
 * Вкладка Raw Data — всё, что приехало из SEON, одним плоским JSON плюс
 * `Copy JSON` (§5 AMEND-10).
 *
 * Плоско и как есть: ключи — имена колонок выгрузки, значения не форматируются.
 * Это тот экран, ради которого аналитик и открывает Raw Data — увидеть сырое
 * поле, а не его причёсанную версию из Summary.
 */
export function RawData({
  raw,
  loading,
  error,
  onRetry,
}: {
  raw: Record<string, unknown> | null
  loading: boolean
  error: string | null
  onRetry: () => void
}) {
  const [copied, setCopied] = React.useState(false)

  // Один и тот же текст для показа и для буфера — чтобы «скопировал» и «вижу»
  // не разъезжались. Пересобираем только при смене данных.
  const text = React.useMemo(() => (raw ? JSON.stringify(raw, null, 2) : ""), [raw])
  const count = raw ? Object.keys(raw).length : 0

  async function copy() {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Буфер обмена недоступен (нет разрешения / небезопасный контекст) —
      // молча не роняем вкладку: текст всё равно виден и выделяется мышью.
    }
  }

  if (loading) {
    return (
      <section className="animate-panel-in rounded border border-line bg-surface p-4 shadow-panel">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-3 h-64 w-full" />
      </section>
    )
  }

  if (error) {
    return (
      <section className="animate-panel-in rounded border border-line bg-surface p-8 text-center shadow-panel">
        <p className="text-sm font-medium text-decline">{error}</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-fg-muted">
          Raw-данные не загрузились. Вкладка остаётся пустой намеренно — устаревшего JSON
          она не показывает.
        </p>
        <Button className="mt-4" onClick={onRetry}>
          Retry
        </Button>
      </section>
    )
  }

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <span className="stencil-label text-fg">
          Raw data · {formatInt(count)} columns
        </span>
        <Button size="sm" onClick={copy} disabled={!text}>
          {copied ? (
            <Check className="h-3.5 w-3.5" strokeWidth={2} />
          ) : (
            <Copy className="h-3.5 w-3.5" strokeWidth={2} />
          )}
          {copied ? "Copied" : "Copy JSON"}
        </Button>
      </header>
      <pre className="max-h-[70vh] overflow-auto px-4 py-3 font-mono text-xs leading-relaxed text-fg">
        {text}
      </pre>
    </section>
  )
}
